/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import {gmAPI, MapService} from '@/services/MapService';
import {Graphic, SubscriptionEvent, SubscriptionResult} from '../Graphic';
import {SVGOverlayView} from '../SVGOverlayView';
import {SubscriptionManager} from '@/lib/filters/subscriptions';
import {computeOffset} from './buildingGeometry';
import {getRotatorCursor, getScaleCursor, getEdges, SQUARE_SVG} from '../editUtilities';
import {TextOverlayView} from '../TextOverlayView/TextOverlayView';

import neRotate from '@/assets/neRotate.png';
import nwRotate from '@/assets/nwRotate.png';
import seRotate from '@/assets/seRotate.png';
import swRotate from '@/assets/swRotate.png';

type BlockPlanGraphicData = {
  buildingType: 'crossDock' | 'singleLoad';
  center: google.maps.LatLngLiteral;
  buildingNumber: number;
  width?: number;
  widthInFeet?: number;
  length?: number;
  lengthInFeet?: number;
  rotation?: number;
};

type BuildingConfig = {
  bayWidth: number;
  minWidth: number;
  minLength: number;
  maxWidth: number;
  maxLength: number;
  startingWidth: number;
  startingLength: number;
  parkingLots: string[];
  widths: number[];
  startingWidthInFeet: number;
  lengths: number[];
  startingLengthInFeet: number;
  buildingWidth: DimensionFunction;
  buildingLength: DimensionFunction;
  speedbayWidth?: number;
  bayLength?: number;
};

type BlockPlanConfig = BuildingConfig & {
  parkingOffset: number;
  parkingDepth: number;
  bayLength: number;
  speedbayWidth: number;
  truckCourtDepth: number;
  startingRotation: number;
};

type DimensionFunction = (dimensionComponent: number, config: BuildingConfig) => number;

type RotatorCircle = {
  path: string;
  fillOpacity: number;
  strokeWeight: number;
  scale: number;
  strokeColor?: string;
};

// Square for the building's edge markers
const square = {...SQUARE_SVG};
square.strokeWeight = 0.001;
// Transparent circle for the building's corner rotators
const rotatorCircle: RotatorCircle = {
  path: 'M 0, 0 m -200, 0 a 200,200 0 2,0 400,0 a 200,200 0 2,0 -400,0',
  fillOpacity: 0,
  strokeWeight: 2,
  scale: 0.1,
};

const generateLengths = (start: number, end: number, step: number) : number[] => {
  const lengths = [];
  for (let i = start; i <= end; i += step) {
    lengths.push(i);
  }
  return lengths;
};

const typeConfigs: Record<'crossDock' | 'singleLoad', BuildingConfig> = {
  crossDock: {
    bayWidth: 50,
    minWidth: 8,
    minLength: 14,
    maxWidth: 12,
    maxLength: 63,
    startingWidth: 11,
    startingLength: 29,
    parkingLots: ['top', 'bottom', 'left', 'right'],
    widths: [420, 470, 520, 570, 620],
    startingWidthInFeet: 570,
    lengths: generateLengths(728, 3276, 52),
    startingLengthInFeet: 1508,
    buildingWidth: (width: number, config) => ((width - 2) * config.bayWidth) + 2 * (config.speedbayWidth ?? 1),
    buildingLength: (length: number, config) => length * (config.bayLength ?? 1),
  },
  singleLoad: {
    bayWidth: 52,
    minWidth: 5,
    minLength: 10,
    maxWidth: 6,
    maxLength: 30,
    startingWidth: 5,
    startingLength: 20,
    parkingLots: ['top', 'bottom'],
    widths: [200, 260, 268, 276, 284, 310, 320],
    startingWidthInFeet: 268,
    lengths: generateLengths(520, 1560, 52),
    startingLengthInFeet: 1040,
    buildingWidth: (width: number, config) => (width - 1) * config.bayWidth + (config.speedbayWidth ?? 1),
    buildingLength: (length: number, config) => length * (config.bayLength ?? 1),
  },
};

/** The angles the corners are from the center of a Block Plan with zero rotation. */
const CANONICAL_CORNER_ANGLES = {
  // Southwest
  0: 225,
  // Northwest
  1: 315,
  // Northeast
  2: 45,
  // Southeast
  3: 135,
} as Record<number, number>;

/** The angles the edges are from the center of a Block Plan with zero rotation. */
const CANONICAL_EDGE_ANGLES = {
  // West
  0: 270,
  // North
  1: 0,
  // East
  2: 90,
  // South
  3: 180,
} as Record<number, number>;

/**
 * @param width in feet
 * @param height in feet
 * @param center
 */
const getBounds = (width: number, height: number, center: google.maps.LatLngLiteral) : google.maps.LatLngBounds => {
  const halfWidthInPixels = MapService.feetToVerticalPixels(width/2);
  const halfHeightInPixels = MapService.feetToHorizontalPixels(height/2);
  const centerPixel = MapService.latLngToScreenPixel(center);
  const nwPixel = new gmAPI.Point(centerPixel.x - halfHeightInPixels, centerPixel.y + halfWidthInPixels);
  const sePixel = new gmAPI.Point(centerPixel.x + halfHeightInPixels, centerPixel.y - halfWidthInPixels);
  return new gmAPI.LatLngBounds(
    MapService.screenPixelToLatLng(nwPixel.x, nwPixel.y),
    MapService.screenPixelToLatLng(sePixel.x, sePixel.y),
  );
};

/**
 * @param width in feet
 */
const getTextOverlayScale = (width: number): number => {
  if (width < 250) {
    return 0.5;
  } else if (width < 300) {
    return 0.75;
  }
  return 1;
};
class BlockPlanGraphic extends Graphic {
  config: BlockPlanConfig;
  buildingType: 'crossDock' | 'singleLoad';
  _clickable: boolean;
  _editing: boolean;
  _draggingParkingIndex?: number;
  widthInFeet: number; // the width value nearest to one in the widths array
  lengthInFeet: number; // the length value nearest to one in the lengths array
  buildingNumber: number;
  center: google.maps.LatLngLiteral; // The center of the building on the map.
  rotation: number;
  zIndex: number; // The z-index of this Graphic's SVG representation.
  subscriptions: SubscriptionManager;
  buildingSvg: SVGOverlayView;
  parkingLotSvgs: SVGOverlayView[];
  buildingPoly: google.maps.Polygon;
  textOverlay: TextOverlayView;
  parkingLotPolys: google.maps.Polygon[];
  edges: google.maps.Marker[];
  rotators: google.maps.Marker[];
  constructor({
    buildingType,
    width,
    widthInFeet,
    length,
    lengthInFeet,
    center,
    rotation,
    buildingNumber,
  }: BlockPlanGraphicData) {
    super();

    this.config = {
      parkingOffset: 20,
      parkingDepth: 65,
      bayLength: 52,
      speedbayWidth: 60,
      truckCourtDepth: 190,
      startingRotation: 0,
      ...typeConfigs[buildingType],
    };

    this.buildingType = buildingType;
    this._clickable = true;
    this._editing = false;
    // use widthInFeet if it is from the database, otherwise check for normal width
    if (widthInFeet) {
      this.widthInFeet = widthInFeet;
    } else if (width) {
      this.widthInFeet = this.config.buildingWidth(width, this.config);
    } else {
      this.widthInFeet = this.config.startingWidthInFeet;
    }
    // use lengthInFeet if it is from the database, otherwise check for normal length
    if (lengthInFeet) {
      this.lengthInFeet = lengthInFeet;
    } else if (length) {
      this.lengthInFeet = this.config.buildingLength(length, this.config);
    } else {
      this.lengthInFeet = this.config.startingLengthInFeet;
    }
    this.buildingNumber = buildingNumber;
    this.center = center;
    this.rotation = rotation ?? 0;
    this.zIndex = 0;
    this.subscriptions = new SubscriptionManager();

    const buildingPath = this.getBuildingPath();
    buildingPath.push(buildingPath[0]);
    this.buildingSvg = new SVGOverlayView({
      path: buildingPath,
      strokeColor: '#323232',
      fillColor: '#FF8000',
      clickable: true,
      strokeWeight: 2,
    }, this.uuid);
    this.buildingSvg.setMap(MapService.getMap());

    const parkingPaths = this.getParkingPaths();
    this.parkingLotSvgs = this.config.parkingLots.map((lot, i) => {
      parkingPaths[i].push(parkingPaths[i][0]);
      return new SVGOverlayView({
        path: parkingPaths[i],
        strokeColor: '#323232',
        fillColor: '#323232',
        clickable: true,
        strokeWeight: 2,
      }, `${this.uuid}_${lot}`);
    });
    this.parkingLotSvgs.forEach((svg) => svg?.setMap(MapService.getMap()));

    this.buildingPoly = new gmAPI.Polygon({
      paths: buildingPath,
      fillColor: '#FF8000',
      draggable: true,
      clickable: true,
      fillOpacity: 0,
      strokeWeight: 1,
    });

    this.buildingPoly.addListener('drag', () => {
      this.drag();
    });
    this.buildingPoly.addListener('dragend', () => {
      this.subscriptions.publish('graphic_changed');
    });

    const bounds = getBounds(this.widthInFeet, this.lengthInFeet, this.center);

    this.textOverlay = new TextOverlayView({
      center: this.center,
      bounds,
      scale: getTextOverlayScale(this.widthInFeet),
      alignment: 'center',
      verticalAlignment: 'center',
      text: this.getBuildingString(),
      backgroundColor: 'rgba(0,0,0,0)',
      borderColor: 'rgba(0,0,0,0)',
      rotation: this.rotation,
    });

    this.parkingLotPolys = parkingPaths.map((path) => {
      return new gmAPI.Polygon({
        paths: path,
        fillColor: '#323232',
        draggable: true,
        clickable: true,
        fillOpacity: 0.5,
        strokeWeight: 1,
      });
    });

    // Clean up any existing listeners to avoid leaks/duplicates when polygons are recreated
    type ParkingLotPolyListenersHolder = {parkingLotPolyListeners?: google.maps.MapsEventListener[][]};
    (this as ParkingLotPolyListenersHolder)
      .parkingLotPolyListeners?.forEach((listeners: google.maps.MapsEventListener[]) => {
        listeners.forEach((h) => {
          try {
            h.remove();
          } catch (e) {
            console.warn('Error removing parking lot polygon listener', e);
          }
        });
      });
    (this as ParkingLotPolyListenersHolder).parkingLotPolyListeners = [];

    // Attach listeners and keep their MapsEventListener handles so they can be removed later
    this.parkingLotPolys.forEach((poly) => {
      const dragHandle = poly.addListener('drag', () => {
        this.drag();
      }) as google.maps.MapsEventListener;
      const dragEndHandle = poly.addListener('dragend', () => {
        this.subscriptions.publish('graphic_changed');
      }) as google.maps.MapsEventListener;

      (this as ParkingLotPolyListenersHolder)?.parkingLotPolyListeners?.push([dragHandle, dragEndHandle]);
    });

    // Expose a disposable for teardown/update paths to call when polygons are destroyed/recreated
    const listenerHolder = this as ParkingLotPolyListenersHolder & {disposeParkingLotPolyListeners?: () => void};
    listenerHolder.disposeParkingLotPolyListeners = () => {
      listenerHolder.parkingLotPolyListeners?.forEach((listeners: google.maps.MapsEventListener[]) => {
        listeners.forEach((h) => {
          try {
            h.remove();
          } catch (e) {
            console.warn('Error removing parking lot polygon listener', e);
          }
        });
      });
      listenerHolder.parkingLotPolyListeners = [];
    };
    // when the map idles, check if the zoom level has changed by more than 5
    let previousZoom = MapService.getMap().getZoom();
    MapService.addMapListener(`block_plan_${this.uuid}_map_idle`, 'idle', () => {
      const zoom = MapService.getMap().getZoom();
      // if the zoom level has changed more than 5 units, update the graphic
      if (Math.abs((zoom ?? 0) - (previousZoom ?? 0)) > 5) {
        this.updateGraphic();
      }
      previousZoom = zoom;
    });

    this.edges = this.getEdges().map((c, i) => {
      square.fillColor = 'white';
      const marker = new gmAPI.Marker({
        position: c,
        zIndex: 2,
        icon: square,
        cursor: getScaleCursor(this.rotation, CANONICAL_EDGE_ANGLES[i]),
      });
      marker.addListener('drag', (e: google.maps.MapMouseEvent) => {
        this.edgeScale(e, i);
      });
      marker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
        this.edgeScale(e, i);
        this.subscriptions.publish('graphic_changed');
      });
      return marker;
    });

    this.rotators = this.getBuildingPath().map((c, i) => {
      const coloredCircle = rotatorCircle;
      coloredCircle.strokeColor = '#FF00FF00';
      const marker = new gmAPI.Marker({
        position: c,
        zIndex: 1,
        icon: coloredCircle,
        cursor: getRotatorCursor(this.rotation, CANONICAL_CORNER_ANGLES[i]),
      });
      marker.addListener('drag', (e: google.maps.MapMouseEvent) => {
        this.rotate(e, i);
      });
      marker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
        this.rotate(e, i);
        this.subscriptions.publish('graphic_changed');
      });
      return marker;
    });
    this.textOverlay.setPointerEvents(false);
  }

  getToolbarOptions() {
    return {
      toolbarOptions: [],
    };
  }

  rotate(event: google.maps.MapMouseEvent, index: number) {
    if (!event.latLng) return;
    const edgeNewXy = MapService.latLngToScreenPixel(event.latLng);
    const cornerXy = MapService.latLngToScreenPixel(this.getBuildingPath()[index]);
    const buildingCenterXy = MapService.latLngToScreenPixel(this.center);
    const angle = Math.atan2(edgeNewXy.x - buildingCenterXy.x, edgeNewXy.y - buildingCenterXy.y);
    const oldAngle = Math.atan2(cornerXy.x - buildingCenterXy.x, cornerXy.y - buildingCenterXy.y);
    this.rotation += oldAngle - angle;
    this.updateGraphic();
  }

  drag() {
    const latOf = (p: google.maps.LatLng | google.maps.LatLngLiteral) =>
      (typeof p.lat === 'function' ? p.lat() : p.lat);
    const lngOf = (p: google.maps.LatLng | google.maps.LatLngLiteral) =>
      (typeof p.lng === 'function' ? p.lng() : p.lng);

    // Expected positions computed from the current center
    const expectedBuildingPath = this.getBuildingPath();
    const expectedParkingPaths = this.getParkingPaths();

    // Actual positions from editable polygons
    const actualBuildingPath = this.buildingPoly.getPath().getArray();
    const actualParkingPaths = this.parkingLotPolys.map((poly) => poly.getPath().getArray());

    // Helper to determine if two points differ meaningfully
    const moved = (a: google.maps.LatLng | google.maps.LatLngLiteral,
      b: google.maps.LatLng | google.maps.LatLngLiteral) => {
      const dLat = Math.abs(latOf(a) - latOf(b));
      const dLng = Math.abs(lngOf(a) - lngOf(b));
      return dLat > 1e-9 || dLng > 1e-9;
    };

    // 1) If the building polygon itself was dragged, use original behavior
    if (moved(actualBuildingPath[0], expectedBuildingPath[0])) {
      const newLat = this.center.lat + latOf(actualBuildingPath[0]) - latOf(expectedBuildingPath[0]);
      const newLng = this.center.lng + lngOf(actualBuildingPath[0]) - lngOf(expectedBuildingPath[0]);
      this.center = new gmAPI.LatLng(newLat, newLng).toJSON();
      this.updateGraphic();
      return;
    }

    // 2) Otherwise, check if a parking lot drag was explicitly flagged by the listener
    //    (this._draggingParkingIndex) and apply that movement if present
    const draggingIndex = this._draggingParkingIndex;
    if (draggingIndex && Number.isInteger(draggingIndex) &&
      draggingIndex >= 0 &&
      draggingIndex < actualParkingPaths.length) {
      // clear the flag so subsequent drags don't reuse it
      delete this._draggingParkingIndex;

      const actual = actualParkingPaths[draggingIndex];
      const expected = expectedParkingPaths[draggingIndex];
      if (actual && expected && moved(actual[0], expected[0])) {
        const newLat = this.center.lat + latOf(actual[0]) - latOf(expected[0]);
        const newLng = this.center.lng + lngOf(actual[0]) - lngOf(expected[0]);
        this.center = new gmAPI.LatLng(newLat, newLng).toJSON();
        this.updateGraphic();
        return;
      }
    }

    // 3) Fallback for backward compatibility: scan each parking lot polygon to see which one was moved
    for (let i = 0; i < actualParkingPaths.length; i += 1) {
      const actual = actualParkingPaths[i];
      const expected = expectedParkingPaths[i];
      if (!actual || !expected) continue;
      if (moved(actual[0], expected[0])) {
        const newLat = this.center.lat + latOf(actual[0]) - latOf(expected[0]);
        const newLng = this.center.lng + lngOf(actual[0]) - lngOf(expected[0]);
        this.center = new gmAPI.LatLng(newLat, newLng).toJSON();
        this.updateGraphic();
        return;
      }
    }

    // If nothing obvious moved, fallback to original simple approach (keep behavior safe)
    const oldPos = expectedBuildingPath;
    const newPos = actualBuildingPath;
    const newLat = this.center.lat + latOf(newPos[0]) - latOf(oldPos[0]);
    const newLng = this.center.lng + lngOf(newPos[0]) - lngOf(oldPos[0]);
    this.center = new gmAPI.LatLng(newLat, newLng).toJSON();
    this.updateGraphic();
  }

  setPointerEvents(enabled: boolean) {
    this.buildingSvg.setPointerEvents(enabled);
    this.parkingLotSvgs.forEach((svg) => svg.setPointerEvents(enabled));
  }

  /**
   * Returns Lat/Lng coordinates for the corners of the building.
   */
  getBuildingPath() : google.maps.LatLng[] {
    const buildingCenterXy = MapService.latLngToScreenPixel(this.center);
    const halfLength = MapService.feetToHorizontalPixels(this.lengthInFeet / 2);
    const halfWidth = MapService.feetToVerticalPixels(this.widthInFeet / 2);
    const rotation = this.rotation;

    const swOffset = computeOffset(buildingCenterXy, halfWidth, -halfLength, rotation);
    const nwOffset = computeOffset(buildingCenterXy, -halfWidth, -halfLength, rotation);
    const neOffset = computeOffset(buildingCenterXy, -halfWidth, halfLength, rotation);
    const seOffset = computeOffset(buildingCenterXy, halfWidth, halfLength, rotation);

    const sw = MapService.screenPixelToLatLng(swOffset.x, swOffset.y) as google.maps.LatLng;
    const nw = MapService.screenPixelToLatLng(nwOffset.x, nwOffset.y) as google.maps.LatLng;
    const ne = MapService.screenPixelToLatLng(neOffset.x, neOffset.y) as google.maps.LatLng;
    const se = MapService.screenPixelToLatLng(seOffset.x, seOffset.y) as google.maps.LatLng;

    return [sw, nw, ne, se];
  }

  /**
   * Returns Lat/Lng coordinates for the corners of each parking lot.
   */
  getParkingPaths() : google.maps.LatLng[][] {
    const buildingCenterXy = MapService.latLngToScreenPixel(this.center);
    const bayLength = this.config.bayLength;
    const speedbayWidth = this.config.speedbayWidth;
    const truckCourtDepth = this.config.truckCourtDepth;
    const parkingOffset = this.config.parkingOffset;
    const parkingDepth = this.config.parkingDepth;
    const rotation = this.rotation;

    if (this.buildingType === 'crossDock') {
      const halfLengthMinusOneBay = MapService.feetToHorizontalPixels((this.lengthInFeet / 2) - bayLength);
      const nearWidth = MapService.feetToVerticalPixels(this.widthInFeet / 2);
      const farWidth = MapService.feetToVerticalPixels((this.widthInFeet / 2) + truckCourtDepth);

      const swOffset = computeOffset(buildingCenterXy, nearWidth, -halfLengthMinusOneBay, rotation);
      const nwOffset = computeOffset(buildingCenterXy, farWidth, -halfLengthMinusOneBay, rotation);
      const neOffset = computeOffset(buildingCenterXy, farWidth, halfLengthMinusOneBay, rotation);
      const seOffset = computeOffset(buildingCenterXy, nearWidth, halfLengthMinusOneBay, rotation);

      let sw = MapService.screenPixelToLatLng(swOffset.x, swOffset.y);
      let nw = MapService.screenPixelToLatLng(nwOffset.x, nwOffset.y);
      let ne = MapService.screenPixelToLatLng(neOffset.x, neOffset.y);
      let se = MapService.screenPixelToLatLng(seOffset.x, seOffset.y);

      const bottom = [sw, nw, ne, se] as google.maps.LatLng[];

      const topSwOffset = computeOffset(buildingCenterXy, -nearWidth, -halfLengthMinusOneBay, rotation);
      const topNwOffset = computeOffset(buildingCenterXy, -farWidth, -halfLengthMinusOneBay, rotation);
      const topNeOffset = computeOffset(buildingCenterXy, -farWidth, halfLengthMinusOneBay, rotation);
      const topSeOffset = computeOffset(buildingCenterXy, -nearWidth, halfLengthMinusOneBay, rotation);

      sw = MapService.screenPixelToLatLng(topSwOffset.x, topSwOffset.y);
      nw = MapService.screenPixelToLatLng(topNwOffset.x, topNwOffset.y);
      ne = MapService.screenPixelToLatLng(topNeOffset.x, topNeOffset.y);
      se = MapService.screenPixelToLatLng(topSeOffset.x, topSeOffset.y);

      const top = [sw, nw, ne, se] as google.maps.LatLng[];

      const halfLengthPlusOffset = MapService.feetToHorizontalPixels((this.lengthInFeet / 2) + parkingOffset);
      const halfWidth = MapService.feetToVerticalPixels(this.widthInFeet / 2);

      const halfLengthPlusOffsetAndDepth =
      MapService.feetToHorizontalPixels((this.lengthInFeet / 2) + parkingOffset + parkingDepth);

      const leftSwOffset = computeOffset(buildingCenterXy, halfWidth, -halfLengthPlusOffsetAndDepth, rotation);
      const leftNwOffset = computeOffset(buildingCenterXy, -halfWidth, -halfLengthPlusOffsetAndDepth, rotation);
      const leftNeOffset = computeOffset(buildingCenterXy, -halfWidth, -halfLengthPlusOffset, rotation);
      const leftSeOffset = computeOffset(buildingCenterXy, halfWidth, -halfLengthPlusOffset, rotation);

      sw = MapService.screenPixelToLatLng(leftSwOffset.x, leftSwOffset.y);
      nw = MapService.screenPixelToLatLng(leftNwOffset.x, leftNwOffset.y);
      ne = MapService.screenPixelToLatLng(leftNeOffset.x, leftNeOffset.y);
      se = MapService.screenPixelToLatLng(leftSeOffset.x, leftSeOffset.y);

      const left = [sw, nw, ne, se] as google.maps.LatLng[];

      const rightSwOffset = computeOffset(buildingCenterXy, halfWidth, halfLengthPlusOffsetAndDepth, rotation);
      const rightNwOffset = computeOffset(buildingCenterXy, -halfWidth, halfLengthPlusOffsetAndDepth, rotation);
      const rightNeOffset = computeOffset(buildingCenterXy, -halfWidth, halfLengthPlusOffset, rotation);
      const rightSeOffset = computeOffset(buildingCenterXy, halfWidth, halfLengthPlusOffset, rotation);

      sw = MapService.screenPixelToLatLng(rightSwOffset.x, rightSwOffset.y);
      nw = MapService.screenPixelToLatLng(rightNwOffset.x, rightNwOffset.y);
      ne = MapService.screenPixelToLatLng(rightNeOffset.x, rightNeOffset.y);
      se = MapService.screenPixelToLatLng(rightSeOffset.x, rightSeOffset.y);

      const right = [sw, nw, ne, se] as google.maps.LatLng[];

      return [top, bottom, left, right];
    } else {
      // single load bottom truck court calculation
      const halfLengthMinusOneBay = MapService.feetToHorizontalPixels((this.lengthInFeet / 2) - bayLength);
      const nearWidth = MapService.feetToVerticalPixels((this.widthInFeet / 2));
      const farWidth = MapService.feetToVerticalPixels((this.widthInFeet / 2) + truckCourtDepth);

      const bottomSwOffset = computeOffset(buildingCenterXy, nearWidth, -halfLengthMinusOneBay, rotation);
      const bottomNwOffset = computeOffset(buildingCenterXy, farWidth, -halfLengthMinusOneBay, rotation);
      const bottomNeOffset = computeOffset(buildingCenterXy, farWidth, halfLengthMinusOneBay, rotation);
      const bottomSeOffset = computeOffset(buildingCenterXy, nearWidth, halfLengthMinusOneBay, rotation);

      let sw = MapService.screenPixelToLatLng(bottomSwOffset.x, bottomSwOffset.y);
      let nw = MapService.screenPixelToLatLng(bottomNwOffset.x, bottomNwOffset.y);
      let ne = MapService.screenPixelToLatLng(bottomNeOffset.x, bottomNeOffset.y);
      let se = MapService.screenPixelToLatLng(bottomSeOffset.x, bottomSeOffset.y);

      const bottom = [sw, nw, ne, se] as google.maps.LatLng[];

      // single load top parking area calculation
      const halfWidthPlusOffset =
      MapService.feetToHorizontalPixels((this.widthInFeet + speedbayWidth) / 2 + parkingOffset);

      const halfWidthPlusOffsetAndDepth =
      MapService.feetToHorizontalPixels((this.widthInFeet + speedbayWidth) / 2 + parkingOffset + parkingDepth);

      const halfLength =
      MapService.feetToHorizontalPixels(this.lengthInFeet / 2);

      const topSwOffset = computeOffset(buildingCenterXy, -halfWidthPlusOffset, -halfLength, rotation);
      const topNwOffset = computeOffset(buildingCenterXy, -halfWidthPlusOffsetAndDepth, -halfLength, rotation);
      const topNeOffset = computeOffset(buildingCenterXy, -halfWidthPlusOffsetAndDepth, halfLength, rotation);
      const topSeOffset = computeOffset(buildingCenterXy, -halfWidthPlusOffset, halfLength, rotation);

      sw = MapService.screenPixelToLatLng(topSwOffset.x, topSwOffset.y);
      nw = MapService.screenPixelToLatLng(topNwOffset.x, topNwOffset.y);
      ne = MapService.screenPixelToLatLng(topNeOffset.x, topNeOffset.y);
      se = MapService.screenPixelToLatLng(topSeOffset.x, topSeOffset.y);

      const top = [sw, nw, ne, se] as google.maps.LatLng[];
      return [top, bottom];
    }
  }

  getEdges() : google.maps.LatLng[] {
    return getEdges(this.getBuildingPath());
  }

  getBuildingString() {
    const width = this.widthInFeet;
    const length = this.lengthInFeet;

    const line1 = 'Building ' + this.buildingNumber + ' <br>';
    const line2 = `${width}' x ${length}' <br>`;
    const line3 = `${(Math.floor(width * length / 1000) * 1000).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} sf`;
    return line1 + line2 + line3;
  }

  /**
   * Adjusts the size of the building based on the edge being dragged.
   */
  edgeScale(event: google.maps.MapMouseEvent, index: number) {
    if (!event.latLng) return;
    // Pixel coordinates of the edge being dragged
    const edgeNewXy = MapService.latLngToScreenPixel(event.latLng);
    const oppositeEdge = this.getEdges()[(index + 2) % 4];
    const oppositeEdgeXy = MapService.latLngToScreenPixel(oppositeEdge);

    if (index === 0 || index === 2) {
      const horizontalScreenDist = Math.hypot(
        MapService.horizontalPixelsToFeet(edgeNewXy.x - oppositeEdgeXy.x),
        MapService.verticalPixelsToFeet(edgeNewXy.y - oppositeEdgeXy.y),
      );

      const currentLength = this.lengthInFeet;
      this.lengthInFeet = this.config.lengths.reduce((prev, curr) => {
        return Math.abs(curr - horizontalScreenDist) < Math.abs(prev - horizontalScreenDist) ? curr : prev;
      });

      const newCenter = MapService.latLngToScreenPixel(this.center);

      let lengthDir = -1;
      if (index === 2) {
        lengthDir = 1;
      }

      const lenDirProduct = lengthDir *
        MapService.feetToHorizontalPixels((this.lengthInFeet - currentLength) / 2);

      newCenter.x += lenDirProduct * Math.cos(this.rotation);
      newCenter.y += lenDirProduct * Math.sin(this.rotation);
      this.center = MapService.screenPixelToLatLng(newCenter.x, newCenter.y, true) as google.maps.LatLngLiteral;
    } else if (index === 1 || index === 3) {
      const verticalScreenDist = Math.hypot(
        MapService.horizontalPixelsToFeet(edgeNewXy.x - oppositeEdgeXy.x),
        MapService.verticalPixelsToFeet(edgeNewXy.y - oppositeEdgeXy.y),
      );

      const currentWidth = this.widthInFeet;
      this.widthInFeet = this.config.widths.reduce((prev, curr) => {
        return Math.abs(curr - verticalScreenDist) < Math.abs(prev - verticalScreenDist) ? curr : prev;
      });

      const newCenter = MapService.latLngToScreenPixel(this.center);

      let widthDir = -1;
      if (index === 3) {
        widthDir = 1;
      }
      const widthDirProduct =
        widthDir * MapService.feetToVerticalPixels((this.widthInFeet - currentWidth) / 2);

      newCenter.x -= widthDirProduct * Math.sin(this.rotation);
      newCenter.y += widthDirProduct * Math.cos(this.rotation);
      this.center = MapService.screenPixelToLatLng(newCenter.x, newCenter.y, true) as google.maps.LatLngLiteral;
    }

    this.updateGraphic();
  }

  /**
   * Updates the different visual components of this Graphic.
   * Because setEditing sometimes is called directly after we update the graphic,
   * we need to update both the visual svgs and editable
   */
  updateGraphic() {
    const buildingPath = this.getBuildingPath();
    const parkingPaths = this.getParkingPaths();

    // Update the building and parking lot display svgs
    this.buildingSvg.setOptions({path: [...buildingPath, buildingPath[0]]});
    this.parkingLotSvgs.forEach((svg, i) => svg.setOptions({path: [...parkingPaths[i], parkingPaths[i][0]]}));

    const bounds = getBounds(this.widthInFeet, this.lengthInFeet, this.center);

    // Update the text overlay that displays in edit mode and non-edit mode
    this.textOverlay.setOptions({
      bounds,
      scale: getTextOverlayScale(this.widthInFeet),
      center: this.center,
      rotation: this.rotation,
      text: this.getBuildingString(),
    });

    // Update the editable polys and handles
    this.buildingPoly.setPath(buildingPath);
    this.parkingLotPolys.forEach((p, i) => p.setPath(parkingPaths[i]));
    const edges = this.getEdges();
    this.edges.forEach((e, i) => {
      e.setPosition(edges[i]);
      e.setZIndex(2);
      e.setCursor(getScaleCursor(this.rotation, CANONICAL_EDGE_ANGLES[i]));
    });
    this.rotators.forEach((r, i) => {
      r.setPosition(buildingPath[i]);
      r.setZIndex(1);
      r.setCursor(getRotatorCursor(this.rotation, CANONICAL_CORNER_ANGLES[i]));
    });
  }

  getCornerCursor(cursorLocation: google.maps.LatLng) : string {
    const heading = gmAPI.geometry.spherical.computeHeading(cursorLocation, this.center);
    if (heading < -90) return 'url(' + neRotate + '), auto';
    if (heading < 0) return 'url(' + seRotate + '), auto';
    if (heading < 90) return 'url(' + swRotate + '), auto';
    return 'url(' + nwRotate + '), auto';
  }

  isClickable() : boolean {
    return this._clickable;
  }

  setClickable(clickable: boolean) {
    this._clickable = clickable;
  }

  setMap(map: google.maps.Map) {
    this.buildingSvg.setMap(map);
    this.parkingLotSvgs.forEach((svg) => svg.setMap(map));
    this.textOverlay.setMap(map);

    this.buildingPoly.setMap(null);
    this.parkingLotPolys.forEach((poly) => poly.setMap(null));
  }

  subscribe(eventName: string, callback: (event: SubscriptionEvent) => void) : SubscriptionResult {
    return this.subscriptions.addSubscription(eventName, callback);
  }

  toJSON() {
    const superJson = super.toJSON();
    return {
      ...superJson,
      comps: [],
      buildingNumber: this.buildingNumber,
      bounds: {
        north: this.center.lat,
        south: this.center.lat,
        east: this.center.lng,
        west: this.center.lng,
      },
      graphicData: {
        featureType: `blockPlan-${this.buildingType}`,
        buildingType: this.buildingType,
        center: this.center,
        buildingNumber: this.buildingNumber,
        widthInFeet: this.widthInFeet,
        lengthInFeet: this.lengthInFeet,
        rotation: this.rotation,
      },
    };
  }

  addLocalHandler(eventType: string, handlerName: string, handler: Function) {
    this.buildingSvg.addListener(eventType, handler);
    this.parkingLotSvgs.forEach((svg) => svg.addListener(eventType, handler));
  }

  removeLocalHandler(eventType: string, handlerName: string, handler: Function) {
    this.buildingSvg.removeListener(eventType, handler);
    this.parkingLotSvgs.forEach((svg) => svg.removeListener(eventType, handler));
  }

  setDraggable(draggable: boolean) {
    this.buildingPoly.setDraggable(draggable);
    this.parkingLotPolys.forEach((poly) => poly.setDraggable(draggable));
    this.edges.forEach((e) => e.setDraggable(draggable));
    this.rotators.forEach((r) => r.setDraggable(draggable));
  }

  setEditing(editing: boolean) : boolean {
    this._editing = editing;
    this.setDraggable(editing);

    if (editing) {
      this.buildingSvg.setMap(null);
      this.parkingLotSvgs.forEach((svg) => svg.setMap(null));

      this.textOverlay.setMap(MapService.getMap());
      // Hack: We don't have control over the z-index of Google Polygons, meaning the Text Overlay would appear
      // underneath the building when in edit mode. To get around this, the building Polygon has no fill, so we
      // make the background of the Text Overlay the color of the building when editing.
      // We always want the Text Overlay  to appear on top when editing, and there isn't really a need to find the
      // z-index of the Polygon - we can just set the Text Overlay's index to 1.
      this.textOverlay.setOptions({backgroundColor: '#FF800080', zIndex: 1});


      this.buildingPoly.setMap(MapService.getMap());
      this.parkingLotPolys.forEach((poly) => poly.setMap(MapService.getMap()));
      this.edges.forEach((e) => e.setMap(MapService.getMap()));
      this.rotators.forEach((r) => r.setMap(MapService.getMap()));

      this.subscriptions.publish('open_info_window', {
        toolbar: this.getToolbarOptions(),
        featureType: 'polygon',
      });
    } else {
      this.buildingSvg.setMap(MapService.getMap());
      this.parkingLotSvgs.forEach((svg) => svg.setMap(MapService.getMap()));
      this.textOverlay.setOptions({zIndex: this.zIndex + 1});

      this.buildingPoly.setMap(null);
      this.parkingLotPolys.forEach((poly) => poly.setMap(null));
      this.edges.forEach((e) => e.setMap(null));
      this.rotators.forEach((r) => r.setMap(null));

      const latestBuildingGeometry = this.buildingPoly.getPath().getArray();
      latestBuildingGeometry.push(latestBuildingGeometry[0]);
      this.buildingSvg.setOptions({path: latestBuildingGeometry, fillColor: '#FF8000', strokeColor: '#323232', strokeWeight: 2});
      // Set the Text Overlay background transparent since the SVG can handle the fillColor and the z-index order
      // is correct when not editing.
      this.textOverlay.setOptions({backgroundColor: '#FF800000'});

      const latestParkingGeometry = this.parkingLotPolys.map((poly) => poly.getPath().getArray());
      this.parkingLotSvgs.forEach((svg, i) => {
        latestParkingGeometry[i].push(latestParkingGeometry[i][0]);
        svg.setOptions({path: latestParkingGeometry[i], fillColor: '#323232', strokeColor: '#323232', strokeWeight: 2});
      });
    }

    return false;
  }

  setHighlight(highlight: boolean) {
    this.buildingSvg.setHighlighted(highlight);
    this.parkingLotSvgs.forEach((svg) => svg.setHighlighted(highlight));
  }

  setZIndex(zIndex: number) {
    this.zIndex = zIndex;
    this.buildingSvg.setZIndex(zIndex);
    this.parkingLotSvgs.forEach((svg) => svg.setZIndex(zIndex));
    this.textOverlay.setOptions({zIndex: zIndex + 1});
  }

  getLocation() {
    return this.center;
  }
}

export {
  BlockPlanGraphic,
};
